import React, { FunctionComponent } from "react";
import { ActivityIndicator, View } from "react-native";
import { useQuery } from "@apollo/client";
import { createStackNavigator } from "@react-navigation/stack";
import { useRoute } from "@react-navigation/native";

import BookingLandingScreen from "../screens/AppointmentBookingFlow/BookingLandingScreen";
import ServiceTypeSelectionScreen from "../screens/AppointmentBookingFlow/ServiceTypeSelectionScreen";
import ServiceCategorySelectionScreen from "../screens/AppointmentBookingFlow/ServiceCategorySelectionScreen";
import ServiceSelectionScreen from "../screens/AppointmentBookingFlow/ServiceSelectionScreen";
import DateTimeSelectionScreen from "../screens/AppointmentBookingFlow/DateTimeSelectionScreen";
import ContactInfoScreen from "../screens/AppointmentBookingFlow/ContactInfoScreen";
import BookingConfirmationScreen from "../screens/AppointmentBookingFlow/BookingConfirmationScreen";
import VisitNotAllowedScreen from "../screens/VisitNotAllowedScreen";
import { GET_BOOKABLE_STORE } from "../screens/AppointmentBookingFlow/gql";
import { commonVisitHeader, stepHeader } from "./NewVisitFlow";

import { colors } from "../theme";

const Stack = createStackNavigator();

const totalSteps = 5;

const FlowLoadingScreen = () => (
  <View
    style={{
      flex: 1,
      backgroundColor: colors.black,
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <ActivityIndicator size="large" color={colors.yellow} />
  </View>
);

const FlowUnavailableScreen = ({ navigation }: { navigation: any }) => (
  <VisitNotAllowedScreen
    onBackPress={() => navigation.dangerouslyGetParent?.()?.navigate("Main")}
  />
);

interface AppointmentBookingFlowProps {
  navigation: any;
}

const AppointmentBookingFlow: FunctionComponent<AppointmentBookingFlowProps> = () => {
  const { params } = useRoute<any>();
  const { loading, data, error } = useQuery(GET_BOOKABLE_STORE, {
    variables: { id: params?.storeId },
    skip: !params?.storeId,
  });

  const store = data?.store;
  const bookable = store && store.appointmentsEnabled !== false;

  if (loading || !store || !bookable || error) {
    return (
      <Stack.Navigator screenOptions={{ headerShown: false }}>
        <Stack.Screen
          name="FlowStatus"
          component={loading ? FlowLoadingScreen : FlowUnavailableScreen}
        />
      </Stack.Navigator>
    );
  }

  return (
    <Stack.Navigator
      headerMode="screen"
      screenOptions={{
        headerTitleAlign: "center",
        animationEnabled: true,
        cardStyle: { flex: 1, backgroundColor: colors.black },
      }}
    >
      <Stack.Screen
        name="BookingLanding"
        component={BookingLandingScreen}
        options={{ ...commonVisitHeader, title: "Book a visit", headerLeft: () => null }}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="ServiceTypeSelection"
        component={ServiceTypeSelectionScreen}
        options={stepHeader({ title: "Category", totalSteps, position: 1 })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="ServiceCategorySelection"
        component={ServiceCategorySelectionScreen}
        options={stepHeader({ title: "Location", totalSteps, position: 2 })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="ServiceSelection"
        component={ServiceSelectionScreen}
        options={stepHeader({ title: "Service", totalSteps, position: 3 })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="DateTimeSelection"
        component={DateTimeSelectionScreen}
        options={stepHeader({ title: "Date & time", totalSteps, position: 4 })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="ContactInfo"
        component={ContactInfoScreen}
        options={stepHeader({ title: "Your info", totalSteps, position: 5 })}
        initialParams={{ store }}
      />
      <Stack.Screen
        name="BookingConfirmation"
        component={BookingConfirmationScreen}
        options={{ headerShown: false }}
        initialParams={{ store }}
      />
    </Stack.Navigator>
  );
};

export default AppointmentBookingFlow;
